import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router";
import { useMutation } from "@tanstack/react-query";
import { fetchWithCsrf } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { ApiSuccess, ApiError } from "@/types/api";

export default function ResetPassword() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");

  const resetPassword = useMutation<
    ApiSuccess<null>,
    ApiError,
    { password: string; passwordConfirmation: string }
  >({
    mutationFn: async (data) => {
      return fetchWithCsrf(`/api/passwords/${token}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      });
    },
    onSuccess: () => {
      navigate("/login", {
        state: { message: "Password has been reset. Please log in." },
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    resetPassword.mutate({ password, passwordConfirmation });
  };

  const fieldErrors = resetPassword.error?.errors || {};

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold uppercase tracking-[0.2em] mb-2">Cobar</h1>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Reset your password</p>
        </div>

        <div className="bg-[#191919] border border-[#616161]/20 rounded-lg p-8">
          {resetPassword.isError && resetPassword.error?.message && (
            <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg text-sm mb-6">
              {resetPassword.error.message}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
                autoComplete="new-password"
                required
                placeholder="At least 8 characters"
              />
              {fieldErrors.password && (
                <p className="text-sm text-destructive">
                  {fieldErrors.password[0]}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="passwordConfirmation">Confirm New Password</Label>
              <Input
                id="passwordConfirmation"
                type="password"
                value={passwordConfirmation}
                onChange={(e) => setPasswordConfirmation(e.target.value)}
                autoComplete="new-password"
                required
              />
              {fieldErrors.passwordConfirmation && (
                <p className="text-sm text-destructive">
                  {fieldErrors.passwordConfirmation[0]}
                </p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={resetPassword.isPending}
            >
              {resetPassword.isPending ? "Resetting..." : "Reset Password"}
            </Button>
          </form>
        </div>

        <p className="text-center text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Remember your password?{" "}
          <Link
            to="/login"
            className="text-primary hover:underline"
          >
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
